import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, LinearProgress, TextField, Tooltip, Typography } from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
    FastForwardRounded as SkipIcon,
    Mic as MicIcon,
    ReplayRounded as ReplayIcon,
    StopRounded as StopIcon,
    VolumeOff as VolumeOffIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import InterviewOrb from "./InterviewOrb";
import AudioBars from "./AudioBars";
import useTypewriter from "./useTypewriter";
import { pageBackgroundSx, themedFieldSx } from "./interviewTheme";
import { backButtonSx, ContentSheet, SERIF } from "./RespondentFormKit";

const RED = "#e5484d";

// One open-text question of a Modern (voice) survey, on a screen of its own.
//
// The question is read aloud as the screen appears (the runner only shows it
// once its voice is ready) and typed out in step with the voice. The orb is the
// voice's control and shows what a click does: nothing until the voice starts,
// Stop while it speaks, Replay once it has finished (or was stopped). Skip, while
// it reads, stops the voice and shows the whole question at once. A question
// that isn't read (`voiced` false: quiet mode) is shown in full from the start.
//
// The answer is the `draft`: recorded (a take is transcribed into it) or typed.
// Three buttons: Back, Record, Next — Next stays off while a required question
// has no answer. The answer field only appears once there is an answer to show
// (a take, or one given before) or the respondent asks to type instead.
export default function VoiceQuestionScreen({
    question,
    required = false,
    progress,
    orbMode,
    micStatus,
    draft,
    onDraftChange,
    onBack, // to the question or page before (the greeting, before the first)
    onNext,
    nextLabel,
    backLabel,
    recordLabel,
    onStartRecording,
    onSubmitRecording,
    onStopSpeaking,
    onReplay,
    turnInFlight = false,
    soundBlocked = false,
    onEnableSound,
    ttsLevelRef,
    micAnalyserRef,
    ttsStartedRef,
    ttsProgressRef,
    ttsNoAudioRef,
    utteranceKey,
    voiced = true,
    quiet = false,
    t,
}) {
    const intl = useIntl();
    const [typing, setTyping] = useState(false);
    const isRecording = micStatus === "recording";
    const isTranscribing = micStatus === "transcribing";
    const micBusy = isRecording || isTranscribing;

    // True once this utterance's audio is actually playing.
    const [playbackStarted, setPlaybackStarted] = useState(false);
    useEffect(() => {
        setPlaybackStarted(false);
        if (orbMode !== "speaking") return undefined;
        let raf;
        const tick = () => {
            if (ttsStartedRef && ttsStartedRef.current) {
                setPlaybackStarted(true);
                return;
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [orbMode, utteranceKey, ttsStartedRef]);

    const speaking = orbMode === "speaking" && playbackStarted;
    const orbAction = speaking ? "stop" : orbMode === "listening" && !micBusy ? "replay" : null;
    const effectiveMode = orbMode === "speaking" && !playbackStarted ? "idle" : isRecording ? "listening" : orbMode;

    const typed = useTypewriter({
        text: question || "",
        runKey: voiced ? utteranceKey : null,
        startedRef: ttsStartedRef,
        progressRef: ttsProgressRef,
        noAudioRef: ttsNoAudioRef,
    });

    // Quiet mode turned on mid-question: the rest of it at once.
    const { complete } = typed;
    useEffect(() => {
        if (quiet) complete();
    }, [quiet, complete]);
    const canSkip = voiced && !micBusy && (orbMode === "speaking" || !typed.done);
    const skip = () => {
        onStopSpeaking();
        typed.complete();
    };

    const hasAnswer = (draft || "").trim().length > 0;
    const showField = typing || hasAnswer;
    const canNext = !micBusy && !turnInFlight && (!required || hasAnswer);
    const handleNext = () => {
        if (!canNext) return;
        typed.complete();
        onNext(draft || "");
    };

    const orbLabel =
        orbAction === "stop"
            ? intl.formatMessage({ id: "voice_survey.stop_voice", defaultMessage: "Stop" })
            : intl.formatMessage({ id: "conversation.replay", defaultMessage: "Replay question" });
    const progressPct = Math.round(Math.min(1, Math.max(0, progress || 0)) * 100);
    const pillSx = { textTransform: "none", borderRadius: "999px", px: 3, py: 1.2, fontWeight: 700, minWidth: 0 };

    return (
        <Box sx={{ minHeight: t.screenMinHeight || "100vh", flexGrow: 1, display: "flex", flexDirection: "column", ...pageBackgroundSx(t) }}>
            {/* Progress, when the survey shows it */}
            {t.showProgress !== false && (
                <Box sx={{ px: { xs: 2, sm: 4 }, pt: { xs: 1.5, sm: 2 }, flexShrink: 0 }}>
                    <Box sx={{ maxWidth: 720, mx: "auto", display: "flex", alignItems: "center", gap: 1.25 }}>
                        <LinearProgress
                            variant="determinate"
                            value={progressPct}
                            sx={{ flex: 1, height: 6, borderRadius: 999, backgroundColor: t.progressTrack, "& .MuiLinearProgress-bar": { borderRadius: 999, backgroundColor: t.progressFill } }}
                        />
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: t.pct, flexShrink: 0 }}>{progressPct}%</Typography>
                    </Box>
                </Box>
            )}

            <ContentSheet t={t} maxWidth={880}>
                {/* Top-aligned, like the other screens: the controls stay in view */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", width: "100%", maxWidth: 720, mx: "auto", px: { xs: 2.5, sm: 3 }, pt: { xs: 1.5, sm: 2 }, pb: 3, gap: 1.75 }}>
                    {/* The orb: the voice's control */}
                    <Tooltip title={orbAction ? orbLabel : ""}>
                        <Box
                            component="button"
                            type="button"
                            data-testid="voice-orb"
                            data-action={orbAction || "none"}
                            aria-label={orbAction ? orbLabel : intl.formatMessage({ id: "voice_survey.voice_loading", defaultMessage: "The question is about to be read aloud" })}
                            disabled={!orbAction}
                            onClick={() => {
                                if (orbAction === "stop") onStopSpeaking();
                                else if (orbAction === "replay") onReplay();
                            }}
                            sx={{
                                position: "relative",
                                p: 0,
                                m: 0,
                                border: "none",
                                background: "none",
                                borderRadius: "50%",
                                cursor: orbAction ? "pointer" : "default",
                                outline: "none",
                                "&:focus-visible": { boxShadow: `0 0 0 3px ${t.focusRing}` },
                                "&:hover .voice-orb-action": { transform: "scale(1.08)" },
                            }}
                        >
                            <InterviewOrb mode={effectiveMode} levelRef={ttsLevelRef} insetColor={t.orbInset} glow={t.orbGlow} palette={t.orbPalette} />
                            {orbAction && (
                                <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
                                    <Box
                                        className="voice-orb-action"
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            borderRadius: "50%",
                                            display: "grid",
                                            placeItems: "center",
                                            backgroundColor: "rgba(255,255,255,0.9)",
                                            boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
                                            transition: "transform .15s ease",
                                        }}
                                    >
                                        {orbAction === "stop" ? (
                                            <StopIcon sx={{ fontSize: 32, color: t.orbPalette?.navy || "#00438e" }} />
                                        ) : (
                                            <ReplayIcon sx={{ fontSize: 30, color: t.orbPalette?.navy || "#00438e" }} />
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Tooltip>

                    {soundBlocked && onEnableSound && (
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={onEnableSound}
                            startIcon={<VolumeOffIcon />}
                            data-testid="enable-sound"
                            sx={{ ...pillSx, backgroundColor: t.submitBg, color: t.submitText, "&:hover": { backgroundColor: t.submitHover } }}
                        >
                            <FormattedMessage id="conversation.enable_sound" defaultMessage="Tap to enable sound" />
                        </Button>
                    )}

                    {/* Skip while it reads · the recording cue · live waveform */}
                    <Box sx={{ height: 28, display: "flex", alignItems: "center" }}>
                        {isRecording ? (
                            <Box sx={{ px: 1.75, py: 0.5, borderRadius: "999px", backgroundColor: "rgba(229,72,77,0.12)", color: RED, fontWeight: 700, fontSize: "0.76rem", letterSpacing: "0.06em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.85 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: RED }} />
                                <FormattedMessage id="conversation.recording" defaultMessage="Recording" />
                            </Box>
                        ) : isTranscribing ? (
                            <Typography sx={{ color: t.kicker, fontSize: "0.76rem", letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600 }}>
                                <FormattedMessage id="conversation.transcribing" defaultMessage="Transcribing…" />
                            </Typography>
                        ) : canSkip ? (
                            <Button
                                size="small"
                                onClick={skip}
                                endIcon={<SkipIcon />}
                                data-testid="voice-skip"
                                sx={{ textTransform: "none", borderRadius: "999px", px: 1.5, color: t.kicker, fontWeight: 600, fontSize: "0.82rem" }}
                            >
                                <FormattedMessage id="voice_survey.skip_voice" defaultMessage="Skip" />
                            </Button>
                        ) : null}
                    </Box>
                    <Box sx={{ height: 36, width: 280, maxWidth: "80%", display: isRecording ? "block" : "none" }}>
                        <AudioBars analyserRef={micAnalyserRef} active={isRecording} color={t.progressFill} count={28} sx={{ height: 36 }} />
                    </Box>

                    {/* The question, typed out with the voice */}
                    <Box data-testid="caption-block" onClick={typed.complete} sx={{ width: "100%", textAlign: "center", minHeight: { xs: "4.5rem", sm: "5.4rem" } }}>
                        <Typography
                            sx={{ fontFamily: SERIF, color: t.question, fontWeight: 600, fontSize: { xs: "1.4rem", sm: "1.7rem" }, lineHeight: 1.35 }}
                        >
                            {typed.shown}
                            {required && typed.done && (
                                <Box component="span" aria-hidden="true" sx={{ color: t.danger, ml: 0.5 }}>
                                    *
                                </Box>
                            )}
                        </Typography>
                    </Box>

                    {/* The answer: recorded into the field, or typed */}
                    <Box sx={{ width: "100%" }}>
                        {showField ? (
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={8}
                                autoFocus={typing && !hasAnswer}
                                value={draft || ""}
                                disabled={micBusy}
                                onChange={(e) => onDraftChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleNext();
                                    }
                                }}
                                placeholder={intl.formatMessage({ id: "conversation.type_placeholder", defaultMessage: "Type your answer…" })}
                                inputProps={{ "aria-label": question, "data-testid": "voice-answer" }}
                                sx={themedFieldSx(t)}
                            />
                        ) : (
                            <Box sx={{ textAlign: "center" }}>
                                <Button
                                    variant="text"
                                    onClick={() => {
                                        typed.complete();
                                        setTyping(true);
                                    }}
                                    disabled={micBusy}
                                    data-testid="voice-type-instead"
                                    sx={{ textTransform: "none", color: t.kicker, fontSize: "0.85rem" }}
                                >
                                    <FormattedMessage id="conversation.type_toggle" defaultMessage="Can't use your mic? Type instead" />
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {/* Back · Record · Next */}
                    <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                        <Box sx={{ minWidth: 96 }}>
                            {onBack && (
                                <Button
                                    variant="outlined"
                                    onClick={onBack}
                                    disabled={micBusy || turnInFlight}
                                    startIcon={<ArrowBackIcon />}
                                    data-testid="voice-back"
                                    sx={backButtonSx(t, pillSx)}
                                >
                                    {backLabel || <FormattedMessage id="voice_survey.back" defaultMessage="Back" />}
                                </Button>
                            )}
                        </Box>

                        {isRecording ? (
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={onSubmitRecording}
                                startIcon={<StopIcon />}
                                data-testid="voice-record"
                                sx={{ ...pillSx, color: "#fff", backgroundColor: RED, "&:hover": { backgroundColor: "#d83b40" } }}
                            >
                                <FormattedMessage id="voice_survey.stop_recording" defaultMessage="Stop recording" />
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={() => {
                                    typed.complete();
                                    onStartRecording();
                                }}
                                disabled={isTranscribing || turnInFlight || (orbMode !== "listening" && orbMode !== "speaking")}
                                startIcon={isTranscribing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <MicIcon />}
                                data-testid="voice-record"
                                sx={{
                                    ...pillSx,
                                    color: t.recordBtnText || "#fff",
                                    backgroundColor: t.recordBtnBg || "#128049",
                                    "&:hover": { backgroundColor: t.recordBtnBg || "#128049", filter: "brightness(0.93)" },
                                    "&.Mui-disabled": { backgroundColor: t.submitDisabledBg, color: t.submitDisabledText },
                                }}
                            >
                                {recordLabel || <FormattedMessage id="voice_survey.record" defaultMessage="Record" />}
                            </Button>
                        )}

                        <Box sx={{ minWidth: 96, display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={handleNext}
                                disabled={!canNext}
                                endIcon={<ArrowForwardIcon />}
                                data-testid="voice-next"
                                sx={{
                                    ...pillSx,
                                    backgroundColor: t.formBtnBg,
                                    color: t.formBtnText || "#fff",
                                    boxShadow: t.formBtnShadow,
                                    "&:hover": { backgroundColor: t.formBtnHover },
                                    "&.Mui-disabled": { backgroundColor: t.formBtnBg, color: t.formBtnText || "#fff", opacity: 0.6 },
                                }}
                            >
                                {nextLabel || <FormattedMessage id="voice_survey.continue" defaultMessage="Continue" />}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </ContentSheet>
        </Box>
    );
}
